"use client"

import { useEffect } from "react"
import { useRouter, useParams, usePathname } from "next/navigation"
import { useAuthStore } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { AppSidebar } from "@/components/dashboard/AppSidebar"
import { DashboardTopBar } from "@/components/dashboard/DashboardTopBar"

interface AuthenticatedLayoutProps {
  children: React.ReactNode
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const router = useRouter()
  const pathname = usePathname()
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  const tokens = useAuthStore((s) => s.tokens)
  const { hasSidebar } = usePermissions()

  // Don't show sidebar on dashboard routes (already has its own layout)
  const isDashboardRoute = pathname?.includes("/dashboard")
  // Chromeless routes — render children full-bleed without sidebar/topbar.
  // /quote-pdf/[id] is a full-window React-PDF viewer that needs the whole
  // viewport; the sidebar would shrink the PDF and obscure controls.
  const isChromelessRoute = pathname?.includes("/quote-pdf")

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!tokens?.access_token && !isDashboardRoute) {
      // Allow public routes to be accessed without auth
      // The specific routes are protected by middleware
    }
  }, [tokens, router, locale, isDashboardRoute])

  // If not logged in, just render children (public view)
  if (!tokens?.access_token) {
    return <>{children}</>
  }

  // If on dashboard route, let the dashboard layout handle sidebar.
  // Same for chromeless routes (PDF viewer) — render bare.
  if (isDashboardRoute || isChromelessRoute) {
    return <>{children}</>
  }

  // If logged in and not on dashboard, show sidebar + topbar only on root page
  if (hasSidebar) {
    const isRootPage = pathname === `/${locale}` || pathname === `/${locale}/`
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="flex min-h-svh min-w-0 flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
          {isRootPage && <DashboardTopBar />}
          {children}
        </SidebarInset>
      </SidebarProvider>
    )
  }

  return <>{children}</>
}
